/* eslint-disable no-unused-vars */
import React, { useRef, useState } from 'react'
import { downloadFile } from 'utils/fileUtils'
import { notifyToast } from 'utils/responseUtils'
import { copyToClipboard } from 'utils/mediaUtils'
import { useDispatch } from 'react-redux'
import ReactAudioPlayer from 'react-audio-player'
import FileUpload from 'components/DragAndDrop/FileUpload'
import { uploadMediaAction } from 'store/actions/media.action'
import { image3DExtensions } from 'utils/constants'
import { JsonViewer, Viewer3D } from 'components/index.js'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { Card, CardContent } from '@components/ui/card'
import { Input } from '@components/ui/input'
import { Button } from '@components/ui/button'
import ModelResultSkeleton from './ModelResultSkeleton'

const ModelResultViewNew = ({
  handleDragStart,
  selectedMedia,
  activeMedia,
  setActiveMedia,
  isDeploying,
  generationData,
  defaultSelectedModel,
}) => {
  const dispatch = useDispatch()
  const [pastedUrl, setPastedUrl] = useState('')
  const fileInputRef = useRef(null)

  const handlePastedFileUpload = async (pastedUrl) => {
    if (pastedUrl) {
      const file = await downloadFile(pastedUrl)
      await uploadFile([file])
    } else {
      dispatch(notifyToast('error', 'Please provide a valide URL'))
    }
  }

  const uploadFile = async (files, itemKey = null, single = true) => {
    const formData = new FormData()
    const file = files[0]
    formData.append('media', file)
    dispatch(
      uploadMediaAction(formData, false, async (url, id) => {
        setActiveMedia({ img: url, step: 0 })
      })
    )
  }

  const handleImageDownload = () => {
    if (activeMedia.img || selectedMedia) {
      // Get prompt context if available
      const promptContext = {
        prompt: defaultSelectedModel?.prompt || '',
        mode: 'image',
        title: defaultSelectedModel?.title || '',
      }
      downloadFile(activeMedia.img || selectedMedia, promptContext)
    }
  }

  const handleUrlCopy = () => {
    ;(activeMedia.img || selectedMedia) && dispatch(copyToClipboard(activeMedia.img))
  }

  const generatedResults = generationData?.flatMap(({ generatedResults }) => generatedResults || [])

  return (
    <>
      {isDeploying ? (
        <ModelResultSkeleton />
      ) : (
        <div className={`!w-full flex md:min-h-[300px] lg:min-h-[400px] flex-col relative gap-2 sm:gap-3`}>
          {generationData?.length > 0 &&
            [...((generationData?.length > 0 ? generationData : []) || [])]
              ?.sort((a, b) => a.id - b.id)
              ?.filter((gen) => gen.loadingEnabledP)
              ?.slice(-1)?.length > 0 && (
              <div className="!w-full">
                {[...((generationData?.length > 0 ? generationData : []) || [])]
                  ?.sort((a, b) => a.id - b.id)
                  .filter((gen) => gen.loadingEnabledP)
                  ?.slice(-1)
                  ?.map((gen, i) => {
                    const { progress } = gen
                    return (
                      <div
                        key={i}
                        className="relative min-h-[300px] sm:min-h-[350px] md:min-h-[400px] bg-[#1a1c20] text-white rounded-md p-3 sm:p-4 shadow-md"
                      >
                        <div className="flex flex-col gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <div className="overflow-hidden w-full h-2 bg-black rounded-lg shadow-lg">
                            <div
                              className={`w-1/4 h-full bg-blue-500 bg-gradient-to-r from-green-400 to-blue-500 rounded-full animate-pulse`}
                              style={{ width: `${progress.bar}%` }}
                            ></div>
                          </div>
                          <div className="mr-2 mb-2 text-2xl sm:text-3xl font-bold">
                            {Math.floor(progress.bar || 0)}%
                          </div>
                        </div>
                        <div className="h-[240px] sm:h-[280px] md:h-[400px] flex flex-col justify-between">
                          <p className="mb-3 sm:mb-4 text-sm sm:text-base tracking-wide text-gray-400">
                            Your media is generating and will be done in a few minutes.
                          </p>
                          {progress?.logs?.length > 0 && (
                            <div className="bg-[#1a1c20] w-full">
                              <div
                                className="h-full max-h-[180px] sm:max-h-[220px] md:max-h-[260px] leading-[18px] whitespace-pre-line mb-[20px] sm:mb-[30px] p-[10px] sm:p-[15px] flex custom-scrollbar-vertical-dark text-white flex-col-reverse text-[11px] sm:text-[12.6px] !bg-[#1a1c20]"
                                dangerouslySetInnerHTML={{
                                  __html: progress?.logs.replaceAll('<?,', ''),
                                }}
                              ></div>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
              </div>
            )}
          {generationData?.length > 0 &&
            [...(generationData?.length > 0 ? generationData : [])]?.filter(
              (gen, i, item) => i !== item.length - 1 && gen.loadingEnabledP
            )?.length > 0 && (
              <div className="grid grid-cols-1 gap-3 sm:gap-4 mt-2 md:grid-cols-2 md:my-2">
                {generationData.length > 0 &&
                  [...(generationData?.length > 0 ? generationData : [])]
                    ?.filter((gen, i, item) => i !== item.length - 1 && gen.loadingEnabledP)
                    ?.map((gen, i) => {
                      const { loadingEnabledP } = gen
                      return (
                        loadingEnabledP && (
                          <div
                            key={i}
                            className="bg-[#1a1c20] md:max-h-[200px] lg:max-h-[250px] overflow-hidden text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg group"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex flex-col justify-center items-center max-w-fit">
                                <p className="mt-1 text-xs sm:text-sm font-bold">
                                  {Math.floor(gen.progress.bar || 0)}%
                                </p>
                              </div>
                              <button className="hidden items-center px-3 sm:px-4 py-1 sm:py-2 text-xs font-bold text-gray-300 bg-gray-700 rounded-full outline-none group-hover:flex">
                                <img
                                  src={'/assets/common/delete-white.svg'}
                                  alt="delete"
                                  className="inline-block mr-1 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4"
                                />
                                <span>Cancel</span>
                              </button>
                            </div>
                            <div className="flex flex-col">
                              <h2 className="mb-3 sm:mb-4 text-sm sm:text-base tracking-wide text-gray-400 whitespace-pre-line">
                                Your media is generating and will be done in a few minutes.
                              </h2>
                            </div>
                          </div>
                        )
                      )
                    })}
              </div>
            )}
          <div className="flex flex-col-reverse gap-2 sm:gap-3 w-full carousal-container md:flex-row md:gap-3">
            {Array.isArray(generatedResults) && generatedResults?.length > 1 && (
              <div
                className={`w-full md:w-[20%] md:h-[500px] lg:h-[600px] flex flex-row md:flex-col gap-2 overflow-x-auto md:overflow-y-auto`}
              >
                {generatedResults?.map((item, i) => (
                  <div
                    className={`cursor-pointer w-16 h-16 sm:w-20 sm:h-20 md:w-[125px] md:h-[125px] overflow-hidden flex-shrink-0 rounded ${activeMedia.img == item && 'border-2 sm:border-[3px] border-solid border-blue-400'}`}
                    key={`sm-imgs-${i}`}
                    onClick={() => setActiveMedia((prev) => ({ ...prev, img: item }))}
                  >
                    {['mp4', 'mov', 'avi'].includes(item.split('.').pop()) ? (
                      <video
                        src={item}
                        controls={false}
                        width={'100%'}
                        height={'100%'}
                        className="w-full h-full object-cover"
                      >
                        <track kind="captions" />
                      </video>
                    ) : ['wav', 'mp3'].includes(item.split('.').pop()) ? (
                      <ReactAudioPlayer src={item} controls className="w-full h-full" />
                    ) : image3DExtensions.includes(item.split('.').pop()) ? (
                      <Viewer3D file={item} classNames={'w-full h-full'} />
                    ) : item.split('.').pop() === 'json' ? (
                      <JsonViewer data={item} isLink={true} classNames={'img w-full h-full rounded group'} />
                    ) : (
                      <LazyLoadImage alt="ex" className="w-full h-full object-cover" src={item} />
                    )}
                  </div>
                ))}
              </div>
            )}
            {activeMedia?.img ? (
              <div className={`${generatedResults?.length > 1 ? 'flex-1' : 'w-full'} relative`}>
                <div
                  className="h-[200px] sm:h-[300px] md:h-[400px] lg:h-[500px] xl:h-[600px] w-full overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center"
                  draggable="true"
                  onDragStart={handleDragStart}
                >
                  {['mp4', 'mov', 'avi'].includes(activeMedia?.img?.split('.').pop()) ? (
                    <video
                      src={activeMedia.img}
                      controls
                      width={'100%'}
                      height={'100%'}
                      className="w-full h-full object-cover"
                    >
                      <track kind="captions" />
                    </video>
                  ) : ['wav', 'mp3'].includes(activeMedia?.img?.split('.').pop()) ? (
                    <ReactAudioPlayer src={activeMedia.img} controls className="w-full" />
                  ) : image3DExtensions.includes(activeMedia?.img?.split('.').pop()) ? (
                    <Viewer3D file={activeMedia.img} classNames={'w-full h-full'} />
                  ) : activeMedia?.img?.split('.').pop() === 'json' ? (
                    <JsonViewer data={activeMedia.img} isLink={true} classNames={'img w-full h-full rounded group'} />
                  ) : (
                    <LazyLoadImage
                      alt="Generated content"
                      className="w-full h-full object-contain"
                      src={activeMedia?.img}
                    />
                  )}
                  <>
                    {generatedResults?.length > 0 && (
                      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1 sm:gap-2 justify-center items-center">
                        <button
                          className="flex gap-1 items-center px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm font-semibold text-gray-900 bg-white rounded-full border border-gray-300 hover:bg-gray-100 focus:outline-none transition-colors"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleImageDownload()
                          }}
                        >
                          <img
                            className="w-3 h-3 sm:w-4 sm:h-4"
                            src={'/assets/common/download-icon.svg'}
                            alt="download"
                          />
                          <span className="hidden sm:inline">Download</span>
                        </button>

                        <button
                          className="flex gap-1 items-center px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm font-semibold text-gray-900 bg-white rounded-full border border-gray-300 hover:bg-gray-100 focus:outline-none transition-colors"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleUrlCopy()
                          }}
                        >
                          <img className="w-3 h-3 sm:w-4 sm:h-4" src={'/assets/common/link.svg'} alt="link" />
                          <span className="hidden sm:inline">Link</span>
                        </button>
                      </div>
                    )}
                  </>
                </div>
              </div>
            ) : (
              !defaultSelectedModel && (
                <div className="flex justify-center items-start w-full h-full">
                  <Card className="w-full bg-white border-2 border-dashed border-slate-200">
                    <CardContent className="p-2">
                      <FileUpload
                        uploadFile={uploadFile}
                        className="h-[25vh] sm:h-[30vh] md:h-[43vh] rounded-t-lg"
                        filesToAccept={'image/*, video/*, audio/*'}
                        isSingleFile={true}
                        fileInputRef={fileInputRef}
                        onDropExtraAction={(url) => setActiveMedia({ img: url, step: 0 })}
                      />

                      <div className="flex relative gap-3 sm:gap-4 items-center p-2 px-2 sm:px-3 mb-2 bg-white rounded-md border-2 border-dashed border-slate-200">
                        <img
                          src={'/assets/common/link-chain-icon.svg'}
                          alt="link"
                          className="w-5 h-5 sm:w-6 sm:h-6 md:w-5 md:h-5"
                        />

                        <Input
                          placeholder="Paste URL"
                          type="text"
                          value={pastedUrl}
                          onChange={(e) => setPastedUrl(e.target.value)}
                          onPaste={(e) => setPastedUrl(e.target.value)}
                          className="w-full border-none shadow-none pr-[80px] sm:pr-[100px] bg-transparent py-2 placeholder:text-xs sm:placeholder:text-sm font-medium"
                        />

                        <Button
                          onClick={() => handlePastedFileUpload(pastedUrl, true)}
                          className="absolute right-0 h-full text-white rounded-l-none bg-slate-800 hover:bg-slate-700 text-xs sm:text-sm px-2 sm:px-4"
                        >
                          Upload
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default ModelResultViewNew
